import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { sitesApi } from '../lib/sites';
import { siteControllersApi } from '../lib/site-controllers';
import { areasApi } from '../lib/areas';
import { useAuth } from '../hooks/useAuth';
import SiteControllerControl from '../components/site-controllers/SiteControllerControl';
import SiteBrandingLogo from '../components/SiteBrandingLogo';
import LocationControl from '../components/sites/LocationControl';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Building2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export default function SitesControlPage() {
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const [uiAlert, setUiAlert] = useState<{
    open: boolean;
    title: string;
    message: string;
  }>({ open: false, title: 'Notice', message: '' });

  // Fetch all sites the user has access to (backend filters based on role)
  const { data: sites, isLoading: sitesLoading } = useQuery({
    queryKey: ['sites'],
    queryFn: () => sitesApi.getAll(),
  });

  // Fetch controllers for all sites
  const { data: allControllers, isLoading: controllersLoading } = useQuery({
    queryKey: ['all-controllers', sites?.map(s => s.id)],
    queryFn: async () => {
      if (!sites || sites.length === 0) return [];
      
      // Fetch controllers for all sites in parallel
      const controllerPromises = sites.map(site => 
        siteControllersApi.getBySite(site.id).then(controllers => ({
          siteId: site.id,
          siteName: site.name,
          controllers,
        }))
      );
      return await Promise.all(controllerPromises);
    },
    enabled: !!sites && sites.length > 0,
  });

  const canConfigure = currentUser?.role === 'super_user';

  const setCurrentPresetMutation = useMutation({
    mutationFn: ({ areaId, presetId }: { areaId: string; presetId: string }) =>
      areasApi.setCurrentPreset(areaId, presetId),
    onSuccess: (updatedArea) => {
      // Invalidate areas for the controller that contains this area
      queryClient.invalidateQueries({ queryKey: ['areas', updatedArea.siteControllerId] });
      // Invalidate the specific area
      queryClient.invalidateQueries({ queryKey: ['area', updatedArea.id] });
      // Invalidate presets for this area to refresh any preset-related data
      queryClient.invalidateQueries({ queryKey: ['presets', updatedArea.id] });
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { error?: string } }; message?: string };
      const errorMessage = err.response?.data?.error || err.message || 'Failed to set current preset';
      setUiAlert({ open: true, title: 'Error', message: errorMessage });
    },
  });

  const handleSetCurrentPreset = (areaId: string, presetId: string | null, _presetAddress?: number | null) => {
    if (presetId === null) return;
    setCurrentPresetMutation.mutate({ areaId, presetId });
  };

  if (sitesLoading || controllersLoading) {
    return (
      <div className="px-2 py-3 sm:px-0">
        <Skeleton className="h-8 w-48 mb-6" />
        <div className="space-y-6">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-64" />
          ))}
        </div>
      </div>
    );
  }

  if (!sites || sites.length === 0) {
    return (
      <div className="px-2 py-3 sm:px-0">
        <h1 className="text-2xl sm:text-3xl font-bold font-heading mb-6">Site Controllers</h1>
        <Card>
          <CardContent className="p-12 text-center">
            <p className="text-muted-foreground">No sites available</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const siteEntries = allControllers || [];

  return (
    <>
    <div className="px-2 py-3 sm:px-0">
      <h1 className="text-2xl sm:text-3xl font-bold font-heading mb-6">Site Controllers</h1>

      <div className="mb-8">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-primary" />
              Locations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <LocationControl mapHeightClassName="min-h-[22rem] h-[28rem]" />
          </CardContent>
        </Card>
      </div>
      
      {siteEntries.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <p className="text-muted-foreground">No controllers found for your sites</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-8">
          {siteEntries.map(({ siteId, siteName, controllers }) => {
            const site = sites?.find((s) => s.id === siteId);
            return (
            <div key={siteId}>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between gap-3">
                    <span className="flex items-center gap-2 min-w-0">
                      <Building2 className="h-5 w-5 text-primary shrink-0" />
                      <span className="truncate">{siteName}</span>
                    </span>
                    <SiteBrandingLogo site={site} />
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {controllers.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No controllers configured for this site</p>
                  ) : (
                    <div className="space-y-4">
                      {controllers.map((controller) => (
                        <SiteControllerControl
                          key={controller.id}
                          controller={controller}
                          siteId={siteId}
                          onSetCurrentPreset={handleSetCurrentPreset}
                          canConfigure={canConfigure}
                        />
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          );
          })}
        </div>
      )}
    </div>
    <AlertDialog
      open={uiAlert.open}
      onOpenChange={(open) => setUiAlert((prev) => ({ ...prev, open }))}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{uiAlert.title}</AlertDialogTitle>
          <AlertDialogDescription>{uiAlert.message}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction>OK</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
